import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";
import { memo, useState, useCallback } from "react";
 import { getProgramName } from "@/config/programBranding";

interface Category {
  title: string;
  programName?: string;
  description: string;
  image: string;
  href: string;
  badge?: string;
  count?: string;
}

const categories: Category[] = [
  {
    title: getProgramName("trainer"),
    programName: "Entrenadores Personales",
    description: "Transforma tu cuerpo con coaching personalizado",
    image: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=300&fit=crop&q=80",
    href: "/trainers",
    badge: "Más Popular",
    count: "+800"
  },
  {
    title: getProgramName("nutritionist"),
    programName: "Nutricionistas",
    description: "Planes nutricionales adaptados a tus objetivos",
    image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=300&fit=crop&q=80",
    href: "/nutritionists",
    count: "+300"
  },
  {
    title: getProgramName("physiotherapist"),
    programName: "Fisioterapeutas",
    description: "Recuperación y prevención de lesiones",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=300&fit=crop&q=80",
    href: "/physiotherapists",
    count: "+250"
  },
  {
    title: getProgramName("yoga"),
    programName: "Estudios de Yoga",
    description: "Encuentra tu equilibrio interior",
    image: "https://images.unsplash.com/photo-1545389336-cf090694435e?w=400&h=300&fit=crop&q=80",
    href: "/yoga",
    count: "+150"
  },
  {
    title: getProgramName("crossfit"),
    programName: "Boxes de CrossFit",
    description: "Entrena en comunidad, supera tus límites",
    image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop&q=80",
    href: "/crossfit",
    count: "+200"
  },
  {
    title: getProgramName("gym"),
    programName: "Gimnasios",
    description: "Centros fitness cerca de ti",
    image: "https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=400&h=300&fit=crop&q=80",
    href: "/directory",
    count: "+500"
  },
];

// Optimized category card with lazy loading
const CategoryCard = memo(({ category, index }: { category: Category; index: number }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  return (
    <Link to={category.href} className="group">
      <Card className="overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-xl h-full">
        <div className="relative h-48 overflow-hidden bg-muted">
          <img 
            src={category.image}
            alt={category.title}
            className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            loading={index < 3 ? "eager" : "lazy"}
            decoding="async"
            onLoad={handleImageLoad}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          
          {/* Badge */}
          {category.badge && (
            <Badge className="absolute top-4 left-4 bg-primary">{category.badge}</Badge>
          )}
          
          {/* Count */}
          {category.count && (
            <div className="absolute top-4 right-4 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-full">
              <span className="text-sm font-semibold">{category.count}</span>
            </div>
          )}

          {/* Title on image */}
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-xl font-bold text-white">{category.title}</h3>
            {category.programName && (
              <p className="text-sm text-white/80">{category.programName}</p>
            )}
          </div>
        </div>
        
        <CardContent className="p-4">
          <p className="text-muted-foreground mb-3">{category.description}</p>
          <div className="flex items-center text-primary font-medium group-hover:gap-2 transition-all">
            <span>Explorar</span>
            <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
});

export const CategoryShowcase = memo(() => {
  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-4">Explora el Ecosistema</Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Todo lo que Necesitas para Tu <span className="gradient-text">Bienestar</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Profesionales verificados, centros de calidad y servicios premium en tu ciudad
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => (
            <CategoryCard key={category.href} category={category} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
});
